import api from '@/lib/api';

export interface LoadChunksRequest {
  chunks: string[];
  collectionName: string;
  projectId?: number;
}

export interface LoadChunksResponse {
  success: boolean;
  message?: string;
  documentsCount?: number;
  error?: string;
}

export interface SearchRequest {
  query: string;
  topK?: number;
  collectionName: string;
  projectId?: number;
}

export interface SearchResult {
  content: string;
  metadata: Record<string, any>;
  score: number;
}

export interface SearchResponse {
  success: boolean;
  results?: SearchResult[];
  count?: number;
  error?: string;
}

export interface Document {
  content: string;
  metadata: Record<string, any>;
}

export interface GetDocumentsResponse {
  success: boolean;
  documents?: Document[];
  total?: number;
  limit?: number;
  offset?: number;
  error?: string;
}

export interface BulkDeleteRequest {
  documentIds: string[];
}

export interface BulkDeleteResponse {
  success: boolean;
  message?: string;
  deletedCount?: number;
  error?: string;
}

export interface UpdateDocumentRequest {
  content: string;
}

export interface UpdateDocumentResponse {
  success: boolean;
  message?: string;
  error?: string;
}

class VectorLoaderService {
  private readonly basePath = '/vector-loader';

  async loadChunks(data: LoadChunksRequest): Promise<LoadChunksResponse> {
    try {
      const { projectId, ...requestData } = data;
      const response = await api.post<LoadChunksResponse>(
        `${this.basePath}/load-chunks`,
        requestData,
        { params: projectId ? { projectId } : {} }
      );
      return response.data;
    } catch (error) {
      console.error('Error loading chunks:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to load chunks'
      };
    }
  }

  async search(data: SearchRequest): Promise<SearchResponse> {
    try {
      const { projectId, ...requestData } = data;
      const response = await api.post<SearchResponse>(
        `${this.basePath}/search`,
        requestData,
        { params: projectId ? { projectId } : {} }
      );
      return response.data;
    } catch (error) {
      console.error('Error searching:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to search'
      };
    }
  }

  // Helper method to format search results for display
  formatSearchResults(results: SearchResult[]): string[] {
    return results
      .sort((a, b) => b.score - a.score) // Sort by highest score first
      .map((result, index) => 
        `${index + 1}. ${result.content} (Score: ${result.score.toFixed(4)})`
      );
  }

  // Helper method to chunk text into smaller pieces
  chunkText(text: string, chunkSize: number = 500, overlap: number = 50): string[] {
    const chunks: string[] = [];
    let start = 0;
    
    while (start < text.length) {
      const end = Math.min(start + chunkSize, text.length);
      chunks.push(text.slice(start, end));
      start += chunkSize - overlap;
    }
    
    return chunks;
  }

  // Helper method to chunk text by a specified delimiter
  chunkTextByDelimiter(text: string, delimiter: string = '---'): string[] {
    const chunks = text.split(delimiter);
    return chunks
      .map(chunk => chunk.trim())
      .filter(chunk => chunk.length > 0);
  }

  async getDocuments(collectionName: string, limit: number = 10, offset: number = 0, projectId?: number): Promise<GetDocumentsResponse> {
    try {
      const response = await api.get<GetDocumentsResponse>(
        `${this.basePath}/documents/${collectionName}`,
        { params: { limit, offset, ...(projectId && { projectId }) } }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting documents:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get documents'
      };
    }
  }

  async bulkDeleteDocuments(collectionName: string, documentIds: string[], projectId?: number): Promise<BulkDeleteResponse> {
    try {
      const response = await api.delete<BulkDeleteResponse>(
        `${this.basePath}/documents/${collectionName}`,
        { 
          data: { documentIds },
          params: projectId ? { projectId } : {}
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting documents:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete documents'
      };
    }
  }

  async updateDocument(collectionName: string, documentId: string, content: string, projectId?: number): Promise<UpdateDocumentResponse> {
    try {
      const response = await api.put<UpdateDocumentResponse>(
        `${this.basePath}/documents/${collectionName}/${documentId}`,
        { content },
        { params: projectId ? { projectId } : {} }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating document:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update document'
      };
    }
  }
}

export const vectorLoaderService = new VectorLoaderService();